import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
} from '@shared/schemas';

import type { ExitFieldErrors } from '../domain/exit-form.rules';
import type {
  DeactivateStatus,
  EnrichStatus,
  ExitOperation,
  ExitSaveStatus,
  ExpertValidationStatus,
  LoadStatus,
  NoteOperation,
  NotesSaveStatus,
  ReactivateStatus,
} from '../domain/profile-detail.model';
import type { TypeCategories } from '../domain/profile-detail.rules';

/** Enrichment lifecycle (`error` holds the failure snackbar message). */
export interface EnrichState {
  status: EnrichStatus;
  error: string | null;
}

/** Deactivation lifecycle (`error` holds the failure snackbar message). */
export interface DeactivateState {
  status: DeactivateStatus;
  error: string | null;
}

/**
 * Reactivation lifecycle (#659). `immunised` is true once a scan-deactivated
 * profile is restored: future keyword scans will leave it alone.
 */
export interface ReactivateState {
  status: ReactivateStatus;
  error: string | null;
  immunised: boolean;
}

/**
 * The admin contact-verification toggle (#556).
 *
 * `pending` holds WHICH contact is in flight, not just a boolean: the panel
 * shows two toggles, and a shared flag would grey out both while one is saving.
 * `error` holds the failure snackbar message.
 */
export interface ContactStatusState {
  pending: 'email' | 'phone' | null;
  error: string | null;
}

/**
 * The admin's "Verified by Invyo Expert" check (#754). `pending` lasts until
 * the reloaded profile is in, so the badge stays disabled until it shows the
 * new state. `profileId` says which profile it is about: another one opened
 * meanwhile keeps its badge usable. `error` holds the failure snackbar message.
 */
export interface ExpertValidationState {
  status: ExpertValidationStatus;
  profileId: string | null;
  error: string | null;
}

/**
 * Network tab rows. `loaded` is distinct from `!loading`: the tab's count badge
 * must stay blank until the first load settles, and only then show a real count
 * (including 0). A failed load also lands `loaded: true` with no rows.
 */
export interface CommonNetworkState {
  rows: CommonNetworkProfile[];
  loading: boolean;
  loaded: boolean;
}

/**
 * The admin's "Add to list" dialog (#614): the lists it can pick from, and the
 * POST. `added` holds the list's name once the POST succeeds, for the snackbar;
 * the reload carries the new membership, so there is nothing else to hold.
 */
export interface AddToListState {
  lists: ListResponse[];
  listsStatus: LoadStatus;
  pending: boolean;
  error: string | null;
  added: string | null;
}

/**
 * Redux state for the Profile Detail page. The slice owns the loaded profile, its
 * load lifecycle, the enrichment lifecycle (POST + the "panel is open"
 * acknowledgement) and the common-network tab. Ephemeral UI (active tab, the
 * force-refresh dialog, the live enrichment drawer's open flag) stays local to
 * the page. Every field is serialisable.
 */
/**
 * The admin's "Types" dialog (#652): the profile's types and the ones removed by
 * hand, and which type a Remove / Restore is in flight for.
 */
export interface TypesEditState {
  status: LoadStatus;
  data: ProfileTypesEdit | null;
  pendingType: string | null;
  error: string | null;
}

/**
 * A client's "Types" dialog (#723): the types the caller's licence sees on the
 * profile, which it added, and which type an Add / Remove is in flight for.
 */
export interface LicenceTypesEditState {
  status: LoadStatus;
  data: LicenceProfileTypesEdit | null;
  pendingType: string | null;
  error: string | null;
}

/**
 * The Notes tab (#676, a list since #743): the caller's licence's notes on the
 * profile, newest first, their load lifecycle, and the changes in flight (an
 * add, an edit or a delete), one per profile. `profileId` says whose notes
 * these are, so a response still in flight when the reader moved to another
 * profile is dropped by the reducer instead of being shown there.
 */
export interface NotesState {
  profileId: string | null;
  notes: ProfileNotes | null;
  status: LoadStatus;
  /** Why the load failed; a save's own error is on its entry of `saves`. */
  error: string | null;
  /**
   * The change to the notes of each profile (#743), by profile id: a profile's
   * change never waits for another's, and the tab of a profile reads its own
   * entry only. No entry = idle.
   */
  saves: Record<string, NoteSave>;
}

/** The change to a profile's notes (#743): what, on which note, and how it went. */
export interface NoteSave {
  status: NotesSaveStatus;
  /** Null while idle. */
  operation: NoteOperation | null;
  /** The note edited or deleted; null for an add, or while idle. */
  noteId: string | null;
  error: string | null;
  /**
   * The edit or delete failed because the note no longer exists (404 Note): it
   * has left the list, and the tab closes its editor or delete dialog.
   */
  noteGone: boolean;
  /**
   * Whether the profile's Notes tab is open to react to the outcome. A change
   * in flight outlives its tab (unmounted, another profile, the page left): it
   * stays pending so the tab, when back, shows it busy; an outcome reaching a
   * detached change only drops it (the saga announces a failure itself).
   */
  attached: boolean;
}

/**
 * The admin's change to a profile's exits (#755), on the model of `NoteSave`
 * (#743): one entry per profile, under `exitSaves[profileId]`; no entry = idle.
 * `error` is a failure no field owns; `fieldErrors` the server's refusals that
 * name a field (a 400 rule, the 409 duplicate), shown under it until that field
 * is edited or the dialog closes.
 */
export interface ExitSaveState {
  status: ExitSaveStatus;
  /** Null while idle. */
  operation: ExitOperation | null;
  error: string | null;
  fieldErrors: ExitFieldErrors;
  /** The exit was not found (deleted elsewhere): the dialog on it closes, the reload takes it away. */
  gone: boolean;
  /**
   * Whether the dialog that sent the change is still there to take its
   * outcome. A page reset (the admin left the profile) or the tab unmounting
   * detaches it: it stays pending, so a dialog opened on the profile meanwhile
   * shows it busy, but its outcome is only dropped, never shown in a dialog it
   * was not sent from (the saga announces a failure itself).
   */
  attached: boolean;
}

export interface ProfileDetailState {
  profile: ProfileResponse | null;
  loadStatus: LoadStatus;
  enrich: EnrichState;
  deactivate: DeactivateState;
  reactivate: ReactivateState;
  contactStatus: ContactStatusState;
  expertValidation: ExpertValidationState;
  commonNetwork: CommonNetworkState;
  addToList: AddToListState;
  /** Admin only (#629): each type's main category, null until loaded or when it failed. */
  typeCategories: TypeCategories | null;
  typesEdit: TypesEditState;
  licenceTypesEdit: LicenceTypesEditState;
  notes: NotesState;
  /** The admin's change to each profile's exits (#755), by profile id. */
  exitSaves: Record<string, ExitSaveState>;
}

export const emptyEnrich: EnrichState = { status: 'idle', error: null };

export const emptyDeactivate: DeactivateState = { status: 'idle', error: null };

export const emptyReactivate: ReactivateState = { status: 'idle', error: null, immunised: false };

export const emptyTypesEdit: TypesEditState = { status: 'idle', data: null, pendingType: null, error: null };

export const emptyLicenceTypesEdit: LicenceTypesEditState = { status: 'idle', data: null, pendingType: null, error: null };

export const emptyNotes: NotesState = {
  profileId: null,
  notes: null,
  status: 'idle',
  error: null,
  saves: {},
};

/** A profile without a change to its notes. */
export const idleNoteSave: NoteSave = {
  status: 'idle',
  operation: null,
  noteId: null,
  error: null,
  noteGone: false,
  attached: false,
};

/** What a profile with no exit change reads. */
export const idleExitSave: ExitSaveState = {
  status: 'idle',
  operation: null,
  error: null,
  fieldErrors: {},
  gone: false,
  attached: false,
};

export const emptyContactStatus: ContactStatusState = { pending: null, error: null };

export const emptyExpertValidation: ExpertValidationState = {
  status: 'idle',
  profileId: null,
  error: null,
};

export const emptyCommonNetwork: CommonNetworkState = {
  rows: [],
  loading: false,
  loaded: false,
};

export const emptyAddToList: AddToListState = {
  lists: [],
  listsStatus: 'idle',
  pending: false,
  error: null,
  added: null,
};

export const initialState: ProfileDetailState = {
  profile: null,
  loadStatus: 'idle',
  enrich: emptyEnrich,
  deactivate: emptyDeactivate,
  reactivate: emptyReactivate,
  contactStatus: emptyContactStatus,
  expertValidation: emptyExpertValidation,
  commonNetwork: emptyCommonNetwork,
  addToList: emptyAddToList,
  typeCategories: null,
  typesEdit: emptyTypesEdit,
  licenceTypesEdit: emptyLicenceTypesEdit,
  notes: emptyNotes,
  exitSaves: {},
};
